<template>
  <div class="relative h-full w-full text-center">
    <template v-for="(image, index) in images" :key="image.src">
      <RevButtonBase
        v-if="!image.isExtra"
        v-bind="{
          ...(activeIndex !== index && {
            'aria-hidden': true,
            tabindex: -1,
          }),
        }"
        :aria-label="getButtonAriaLabel"
        class="mx-auto block md:w-full"
        :class="{
          'focus-visible-outline-inset-hi cursor-pointer': allowMediaViewer,
        }"
        @click="emit('click', image, index)"
      >
        <MainImage
          :active-index
          :class="{
            'absolute inset-0 hidden md:block': index !== 0,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
          :decoding="index === 0 ? 'sync' : 'async'"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          :hidden="activeIndex !== index"
          :image
          :index
          :is-mobile
          :loading="index === 0 ? 'eager' : 'lazy'"
          @visible="emit('visible', index)"
        />
        <!-- We use a div with a button role as we can not nest two real buttons.
             When the sticker slot is empty (e.g. sticker disabled), `empty:hidden`
             removes the wrapper from the layout, tab order and pointer events. -->
        <div
          v-if="withSticker && index === 0"
          class="absolute bottom-8 left-8 empty:hidden md:bottom-auto md:left-auto md:right-8 md:top-8"
          role="button"
          tabindex="0"
          @click="handleStickerClick"
          @keypress.enter="handleStickerClick"
        >
          <slot name="sticker" />
        </div>
      </RevButtonBase>
      <div
        v-else-if="image.isExtra && image.type === 'review' && review"
        v-show="activeIndex === index"
        class="mx-auto hidden md:block"
      >
        <Review
          ref="reviewCard"
          :aria-label="reviewAriaLabel"
          :card-class-name="BIG_ITEM_SIZE_CLASS"
          :class="{
            'absolute inset-0': activeIndex === index,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
          is-in-main-container
          :review
          @click="emit('extra-item-click', 'review')"
          @visible="emit('visible', index, 'review')"
        />
      </div>
      <div
        v-else-if="image.isExtra && image.type === 'video'"
        v-show="activeIndex === index"
        class="mx-auto hidden md:block"
      >
        <div
          :class="{
            'absolute inset-0': activeIndex === index,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
        >
          <MarketingVideo
            v-if="videoSource.isSelfHosted"
            ref="videoCard"
            :is-mobile
            :poster="videoSource.poster"
            :product-name
            :sources="videoSource.sources"
            :title="videoSource.title"
            @click="emit('extra-item-click', 'video')"
            @start="emit('visible', index, 'video')"
          />
          <YoutubeVideo
            v-else
            ref="videoCard"
            :aria-label="videoAriaLabel"
            :is-mobile
            @click="emit('extra-item-click', 'video')"
            @start="emit('visible', index, 'video')"
          />
        </div>
      </div>
      <RevButtonBase
        v-else-if="image.isExtra && image.type === 'jpRefurbishmentInfographic'"
        v-bind="{
          ...(activeIndex !== index && {
            'aria-hidden': true,
            tabindex: -1,
          }),
        }"
        :aria-label="jpRefurbishmentInfographicAriaLabel"
        class="mx-auto block md:w-full"
        :class="{
          'focus-visible-outline-inset-hi cursor-pointer': allowMediaViewer,
        }"
        @click="emit('click', image, index)"
      >
        <MainImage
          :active-index
          :class="{
            'absolute inset-0 hidden md:block': index !== 0,
            'invisible opacity-0 md:z-[-1]': activeIndex !== index,
          }"
          :decoding="index === 0 ? 'sync' : 'async'"
          :fetchpriority="index === 0 ? 'high' : 'low'"
          :hidden="activeIndex !== index"
          :image
          :index
          :is-mobile
          :loading="index === 0 ? 'eager' : 'lazy'"
          @visible="emit('visible', index, image.type)"
        />
      </RevButtonBase>
    </template>
  </div>
</template>

<script setup lang="ts">
import { type VNode, computed, useTemplateRef } from 'vue'

import type { Review as ReviewType } from '@backmarket/nuxt-layer-cms/ReviewsView.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButtonBase } from '@ds/components/ButtonBase'

import marketingVideoTranslations from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.translations'
import MarketingVideo from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.vue'
import { useGalleryVideoSource } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/composables/useGalleryVideoSource'

import { BIG_ITEM_SIZE_CLASS } from '../GalleryNew.constants'
import translations from '../GalleryNew.translations'
import type {
  GalleryImage,
  GalleryImageExtraItemType,
} from '../GalleryNew.types'

import MainImage from './MainImage.vue'
import Review from './Review/Review.vue'
import YoutubeVideo from './YoutubeVideo/YoutubeVideo.vue'

const {
  images,
  activeIndex = 0,
  review,
  allowMediaViewer,
  isMobile,
  productName,
  brand,
  categoryId,
} = defineProps<{
  images: GalleryImage[]
  review?: ReviewType | null
  activeIndex?: number
  allowMediaViewer: boolean
  // Only used for tracking and for resetting the active index, not for layout
  isMobile: boolean
  withSticker?: boolean
  /**
   * Product name/model for accessibility labels (e.g., "iPhone 15 Unlocked").
   */
  productName?: string
  brand: string
  categoryId: string
}>()

const emit = defineEmits<{
  (e: 'click', image: GalleryImage, index: number): void
  (e: 'extra-item-click', imageType: GalleryImageExtraItemType): void
  (e: 'visible', index: number, imageType?: GalleryImageExtraItemType): void
  (e: 'sticker-click'): void
}>()

defineSlots<{
  sticker?: (props: { size?: 'small' | 'default' }) => VNode[]
}>()

const i18n = useI18n()

const reviewCard = useTemplateRef('reviewCard')
const videoCard = useTemplateRef('videoCard')

// Resolves YouTube vs self-hosted video + the matching sources/poster/title.
const videoSource = useGalleryVideoSource({ brand, categoryId })

defineExpose({
  reviewCard,
  videoCard,
})

function handleStickerClick(e: Event) {
  e.stopPropagation()
  e.preventDefault()

  emit('sticker-click')
}

const getButtonAriaLabel = computed(() => {
  if (!productName) {
    return undefined
  }

  return i18n(translations.mainImageButtonAriaLabel, {
    productName,
  })
})

const videoAriaLabel = computed(() => {
  if (!productName) return undefined

  return i18n(marketingVideoTranslations.playAriaLabel, { productName })
})

const reviewAriaLabel = computed(() => {
  if (!productName) return undefined

  return i18n(translations.reviewButtonAriaLabel, { productName })
})

const jpRefurbishmentInfographicAriaLabel = computed(() => {
  return i18n(translations.jpRefurbishmentInfographicAlt)
})
</script>
